import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Container,
  useToast,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  Circle,
  Divider,
  Badge,
  Spinner
} from '@chakra-ui/react';
import { CheckIcon, TimeIcon, InfoIcon } from '@chakra-ui/icons';

import StudentRUTValidator from './components/StudentRUTValidator';
import AvailableSlots from './components/AvailableSlots';
import BookingForm from './components/BookingForm';
import BookingConfirmation from './components/BookingConfirmation';
import { getTeacherProfile, validateStudentRUT } from 'services/appointmentBooking';

// Pasos del flujo
const BOOKING_STEPS = {
  VALIDATE_STUDENT: 1,
  SELECT_SLOT:      2,
  FILL_DETAILS:     3,
  CONFIRMATION:     4
};

const STEP_NAMES = {
  [BOOKING_STEPS.VALIDATE_STUDENT]: 'Validar',
  [BOOKING_STEPS.SELECT_SLOT]:      'Horario',
  [BOOKING_STEPS.FILL_DETAILS]:     'Datos',
  [BOOKING_STEPS.CONFIRMATION]:     'Confirmación'
};

function AppointmentBooking() {
  const { teacherId } = useParams();
  const location      = useLocation();

  // RUT pre-seleccionado desde el panel admin (query param ?student_rut=)
  const preselectedRut = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('student_rut') || '';
  }, [location.search]);

  const [currentStep, setCurrentStep] = useState(BOOKING_STEPS.VALIDATE_STUDENT);
  const [studentData, setStudentData]             = useState(null);
  const [selectedSlot, setSelectedSlot]           = useState(null);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [teacherData, setTeacherData] = useState(null);
  const [teacherLoading, setTeacherLoading] = useState(true);
  const [teacherError, setTeacherError] = useState('');

  const toast = useToast();

  // Cargar perfil del profesor al montar
  const loadTeacher = useCallback(async () => {
    if (!teacherId) { setTeacherError('URL inválida: falta el ID del profesor'); setTeacherLoading(false); return; }
    try {
      const res = await getTeacherProfile(teacherId);
      if (res.success) setTeacherData(res.data);
      else setTeacherError(res.message || 'Profesor no encontrado');
    } catch {
      setTeacherError('No se pudo cargar el perfil del profesor');
    } finally {
      setTeacherLoading(false);
    }
  }, [teacherId]);

  useEffect(() => { loadTeacher(); }, [loadTeacher]);

  // Auto-validar alumno pre-seleccionado desde admin una vez que el profesor cargó
  useEffect(() => {
    if (!preselectedRut || teacherLoading || !teacherData) return;
    if (currentStep !== BOOKING_STEPS.VALIDATE_STUDENT) return;

    let cancelled = false;
    const autoValidate = async () => {
      setLoading(true);
      try {
        const res = await validateStudentRUT(preselectedRut);
        if (cancelled) return;
        if (res?.success && res?.data) {
          setStudentData(res.data);
          setCurrentStep(BOOKING_STEPS.SELECT_SLOT);
        } else {
          setError('No se pudo pre-cargar el alumno. Ingrese el RUT manualmente.');
        }
      } catch {
        if (!cancelled) setError('Error al pre-cargar el alumno. Ingrese el RUT manualmente.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    autoValidate();
    return () => { cancelled = true; };
  }, [preselectedRut, teacherLoading, teacherData]); // eslint-disable-line

  // Derivar nombre del sitio leyendo localStorage directamente
  // (no usamos getSiteConfig() porque redirige a /auth/signin para visitantes públicos)
  const siteName = useMemo(() => {
    try {
      const cfg = JSON.parse(localStorage.getItem('site_config') || '{}');
      return (
        cfg?.configs?.find(c => c.key === 'SITE_NAME')?.value ||
        cfg?.name ||
        'Sistema de Agendamiento'
      );
    } catch {
      return 'Sistema de Agendamiento';
    }
  }, []);

  useEffect(() => { setError(''); }, [currentStep]);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleStudentValidated = (student) => {
    setStudentData(student);
    setCurrentStep(BOOKING_STEPS.SELECT_SLOT);
    toast({
      title: 'Estudiante validado',
      description: `Puede reservar horarios para ${student.display_name}`,
      status: 'success', duration: 3000, isClosable: true
    });
  };

  const handleSlotSelected = (slot) => {
    setSelectedSlot(slot);
    setCurrentStep(BOOKING_STEPS.FILL_DETAILS);
  };

  const handleBookingSubmitted = (details) => {
    setAppointmentDetails(details);
    setCurrentStep(BOOKING_STEPS.CONFIRMATION);
    toast({
      title: 'Cita reservada exitosamente',
      description: 'Se ha enviado la confirmación a su email',
      status: 'success', duration: 5000, isClosable: true
    });
  };

  const handleStartOver = () => {
    setCurrentStep(BOOKING_STEPS.VALIDATE_STUDENT);
    setStudentData(null);
    setSelectedSlot(null);
    setAppointmentDetails(null);
    setError('');
  };

  const handleGoBack = () => {
    if (currentStep === BOOKING_STEPS.SELECT_SLOT) {
      setCurrentStep(BOOKING_STEPS.VALIDATE_STUDENT);
      setStudentData(null);
    } else if (currentStep === BOOKING_STEPS.FILL_DETAILS) {
      setCurrentStep(BOOKING_STEPS.SELECT_SLOT);
      setSelectedSlot(null);
    }
  };

  // ─── Step indicator (horizontal bar) ────────────────────────────────────────
  const StepIndicator = () => {
    const steps = Object.entries(STEP_NAMES);
    return (
      <HStack spacing={0} w="full" justify="center" mb={6}>
        {steps.map(([num, name], idx) => {
          const stepNum   = parseInt(num);
          const isActive    = stepNum === currentStep;
          const isCompleted = stepNum < currentStep;

          return (
            <React.Fragment key={stepNum}>
              <VStack spacing={1} align="center" minW="60px">
                <Circle
                  size="28px"
                  bg={isCompleted ? 'green.400' : isActive ? 'blue.500' : 'gray.200'}
                  color="white"
                  fontSize="11px"
                  fontWeight="700"
                >
                  {isCompleted ? <CheckIcon w="10px" h="10px" /> : stepNum}
                </Circle>
                <Text
                  fontSize="10px"
                  fontWeight={isActive ? '700' : '400'}
                  color={isActive ? 'blue.600' : isCompleted ? 'green.500' : 'gray.500'}
                  textAlign="center"
                >
                  {name}
                </Text>
              </VStack>
              {idx < steps.length - 1 && (
                <Box
                  flex="1"
                  h="2px"
                  bg={currentStep > stepNum ? 'green.400' : 'gray.200'}
                  mb="14px"
                  mx={1}
                />
              )}
            </React.Fragment>
          );
        })}
      </HStack>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box bg="gray.50" minH="100vh">

      {/* Minimal top bar */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.200" px={6} py={3}>
        <Text fontSize="sm" fontWeight="600" color="gray.700">{siteName}</Text>
      </Box>

      {/* Error si el profesor no existe */}
      {!teacherLoading && teacherError && (
        <Container maxW="container.sm" py={16}>
          <Alert status="error" borderRadius="xl">
            <AlertIcon />
            <AlertDescription>{teacherError}</AlertDescription>
          </Alert>
        </Container>
      )}

      {/* ── SELECT_SLOT: Calendly-style split panel ── */}
      {currentStep === BOOKING_STEPS.SELECT_SLOT ? (
        <Container maxW="900px" py={8}>
          <Flex
            bg="white"
            borderRadius="xl"
            boxShadow="lg"
            overflow="hidden"
            direction={{ base: 'column', md: 'row' }}
          >
            {/* Left info panel */}
            <Box
              w={{ base: '100%', md: '280px' }}
              minW={{ md: '280px' }}
              bg="gray.50"
              p={6}
              borderRight={{ base: 'none', md: '1px solid' }}
              borderBottom={{ base: '1px solid', md: 'none' }}
              borderColor="gray.200"
            >
              <VStack spacing={4} align="flex-start">
                {/* Avatar del profesor */}
                {teacherData?.avatar_url ? (
                  <Box
                    as="img"
                    src={teacherData.avatar_url}
                    alt={teacherData.display_name}
                    boxSize="56px"
                    borderRadius="full"
                    border="2px solid"
                    borderColor="blue.200"
                  />
                ) : (
                  <Circle size="56px" bg="blue.500" color="white" fontSize="xl" fontWeight="bold">
                    {teacherData?.display_name?.charAt(0)?.toUpperCase() || '?'}
                  </Circle>
                )}

                <Box>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    {siteName}
                  </Text>
                  <Text fontSize="lg" fontWeight="700" color="gray.800" mt={1}>
                    {teacherData ? teacherData.display_name : '—'}
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Reunión con apoderado
                  </Text>
                </Box>

                <HStack spacing={2} color="gray.600">
                  <TimeIcon w="14px" h="14px" />
                  <Text fontSize="sm">30 minutos</Text>
                </HStack>

                {/* Student banner */}
                {studentData && (
                  <Box
                    bg="blue.50"
                    border="1px solid"
                    borderColor="blue.200"
                    borderRadius="md"
                    p={3}
                    w="full"
                  >
                    <Text fontSize="xs" color="blue.600" fontWeight="600">Reservando para:</Text>
                    <Text fontSize="sm" color="blue.800" fontWeight="700" mt="2px" noOfLines={2}>
                      {studentData.first_name && studentData.last_name
                        ? `${studentData.first_name} ${studentData.last_name}`
                        : studentData.display_name}
                    </Text>
                    {studentData.formatted_rut && (
                      <Text fontSize="xs" color="blue.600">RUT: {studentData.formatted_rut}</Text>
                    )}
                    {studentData.group_name && (
                      <Badge colorScheme="blue" fontSize="xs" mt="4px" whiteSpace="normal" textAlign="left">
                        {studentData.group_name}
                      </Badge>
                    )}
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Right slots panel */}
            <Box flex="1" p={6}>
              {error && (
                <Alert status="error" borderRadius="md" mb={4}>
                  <AlertIcon />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <AvailableSlots
                student={studentData}
                teacherId={teacherId}
                teacherName={teacherData?.display_name}
                onSlotSelected={handleSlotSelected}
                loading={loading}
                setLoading={setLoading}
                error={error}
                setError={setError}
              />
            </Box>
          </Flex>

          {/* Back button */}
          <Flex justify="flex-start" mt={4}>
            <Button variant="ghost" size="sm" onClick={handleGoBack} color="gray.600">
              ← Volver
            </Button>
          </Flex>
        </Container>

      ) : (
        /* ── Other steps: centered single column ── */
        <Container maxW="container.sm" py={8}>
          <VStack spacing={6}>

            {/* Step indicator */}
            <Box bg="white" w="full" borderRadius="xl" boxShadow="sm" p={5}>
              <StepIndicator />
            </Box>

            {/* Student info banner */}
            {studentData && currentStep !== BOOKING_STEPS.VALIDATE_STUDENT && (
              <Alert status="info" borderRadius="xl" w="full">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  <HStack spacing={2} flexWrap="wrap">
                    <strong>{studentData.display_name}</strong>
                    {studentData.formatted_rut && (
                      <Text as="span" color="gray.600">RUT: {studentData.formatted_rut}</Text>
                    )}
                    {studentData.group_name && (
                      <Badge colorScheme="blue" fontSize="xs">{studentData.group_name}</Badge>
                    )}
                    {preselectedRut && (
                      <Badge colorScheme="teal" fontSize="xs" variant="subtle">
                        Pre-seleccionado desde admin
                      </Badge>
                    )}
                  </HStack>
                </AlertDescription>
              </Alert>
            )}

            {/* Error */}
            {error && (
              <Alert status="error" borderRadius="md" w="full">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Step content */}
            <Box bg="white" w="full" borderRadius="xl" boxShadow="sm" p={6}>
              {currentStep === BOOKING_STEPS.VALIDATE_STUDENT && (
                /* Si hay RUT pre-seleccionado, mostrar spinner mientras se auto-valida */
                preselectedRut && loading
                  ? (
                    <VStack spacing={3} py={6} align="center">
                      <Spinner size="lg" color="blue.500" />
                      <Text fontSize="sm" color="gray.500">Cargando alumno...</Text>
                    </VStack>
                  )
                  : (
                    <StudentRUTValidator
                      onStudentValidated={handleStudentValidated}
                      loading={loading}
                      setLoading={setLoading}
                      error={error}
                      setError={setError}
                    />
                  )
              )}
              {currentStep === BOOKING_STEPS.FILL_DETAILS && (
                <BookingForm
                  student={studentData}
                  selectedSlot={selectedSlot}
                  teacherName={teacherData?.display_name}
                  onBookingSubmitted={handleBookingSubmitted}
                  loading={loading}
                  setLoading={setLoading}
                  error={error}
                  setError={setError}
                />
              )}
              {currentStep === BOOKING_STEPS.CONFIRMATION && (
                <BookingConfirmation
                  student={studentData}
                  appointmentDetails={appointmentDetails}
                  onStartOver={handleStartOver}
                />
              )}
            </Box>

            {/* Navigation buttons */}
            {currentStep !== BOOKING_STEPS.VALIDATE_STUDENT &&
             currentStep !== BOOKING_STEPS.CONFIRMATION && (
              <Flex justify="space-between" w="full">
                <Button variant="ghost" onClick={handleGoBack} isDisabled={loading} size="sm">
                  ← Volver
                </Button>
                <Button variant="outline" onClick={handleStartOver} isDisabled={loading} size="sm">
                  Comenzar de nuevo
                </Button>
              </Flex>
            )}
          </VStack>
        </Container>
      )}

      {/* Minimal footer */}
      <Box py={6} textAlign="center">
        <HStack spacing={1} justify="center" color="gray.400">
          <InfoIcon w="12px" h="12px" />
          <Text fontSize="xs">¿Problemas para reservar? Contacte directamente al establecimiento</Text>
        </HStack>
      </Box>
    </Box>
  );
}

export default AppointmentBooking;
